import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import type { ApiRequest } from '@mimecast-ui/shared/data-access-core/api-types';
import { ConfigService } from '@mimecast-ui/universal-login/data-access-config';

import { LoginSessionService } from '../services/login-session.service';
import { MfaStateService } from '../services/mfa-state.service';

export type XMcStrictHeaderValue = 'strict' | 'none' | 'pendo' | 'nopendo';

const MFA_URL_MARKERS = [
  '/u/rest/api/login/submit-verification-code',
  '/u/rest/api/login/sso/submit-verification-code',
];

/**
 * Legacy `isApi` in api-interceptor-service.js: any Mimecast API URL, whether it is
 * a login BFF route (`/u/rest/api/...`), an SSST proxy route (`/u/proxy/api/...`), or
 * an authenticated route (`/auth/api/...`). Legacy gated `x-mc-strict` on this alone,
 * which is why BFF login calls carried it.
 */
export function isLoginApiUrl(url: string): boolean {
  return url.includes('/api/');
}

/**
 * Narrower than {@link isLoginApiUrl}: only the proxy routes get `x-mc-date`,
 * `x-mc-app-id`, and the `{ data: [...] }` body wrap. Legacy kept the proxy decision
 * separate from the header decision.
 */
export function isLoginProxyUrl(url: string): boolean {
  return url.includes('/u/proxy/') || url.includes('/auth/api/');
}

/** Legacy interceptor adds `sid` for MFA BFF calls. */
export function needsMfaSidHeader(url: string): boolean {
  return MFA_URL_MARKERS.some((marker) => url.includes(marker));
}

/**
 * Match legacy MUL `payloadFormatter`: wrap plain request objects as `{ data: [payload] }`.
 * Leave bodies that already include `data` (or non-JSON bodies) unchanged.
 */
export function formatLoginApiPayload(body: unknown): unknown {
  if (body == null) {
    return body;
  }

  if (Array.isArray(body)) {
    return { data: body } satisfies ApiRequest<unknown>;
  }

  if (
    typeof body !== 'object' ||
    body instanceof FormData ||
    body instanceof Blob
  ) {
    return body;
  }

  if ('data' in body) {
    return body;
  }

  return { data: [body] } satisfies ApiRequest<unknown>;
}

/**
 * Mirror legacy login-controller.js + api-interceptor-utils.js for x-mc-strict.
 * Truthy cookieSameSiteStrictMode -> strict; explicit false -> none;
 * otherwise pendo when framed in Pendo, else nopendo.
 */
export function resolveXMcStrictHeader(
  cookieSameSiteStrictMode: boolean | undefined,
  isAppRunningInPendo: boolean
): XMcStrictHeaderValue {
  if (cookieSameSiteStrictMode) {
    return 'strict';
  }

  if (cookieSameSiteStrictMode === false) {
    return 'none';
  }

  return isAppRunningInPendo ? 'pendo' : 'nopendo';
}

export const loginApiInterceptor: HttpInterceptorFn = (request, next) => {
  const mfaState = inject(MfaStateService);

  const isProxy = isLoginProxyUrl(request.url);

  // Every current proxy URL also contains `/api/`, but test both so a future
  // `/u/proxy/<non-api>` route cannot silently skip the proxy handling below.
  if (!isLoginApiUrl(request.url) && !isProxy) {
    return next(request);
  }

  const configService = inject(ConfigService);
  const loginSessionService = inject(LoginSessionService);
  const { xMcAppId: appId, loginModule } = configService.resolveAppConfig();

  // The backend reads x-mc-strict to choose the SameSite attribute for `mc-you`,
  // so it has to reach the BFF login calls that mint the cookie, not just the proxy.
  const headers: Record<string, string> = {
    'x-mc-strict': resolveXMcStrictHeader(
      loginModule.cookieSameSiteStrictMode,
      loginSessionService.isAppRunningInPendo()
    ),
    'x-mc-req-id': crypto.randomUUID(),
  };

  const sid = needsMfaSidHeader(request.url) ? mfaState.sessionId() : null;
  if (sid && !request.headers.has('sid')) {
    headers['sid'] = sid;
  }

  if (!isProxy) {
    return next(request.clone({ setHeaders: headers }));
  }

  // Legacy suppressed x-mc-date on the `requiresAuthentication: false` BFF calls, and
  // AuthenticationService already sends x-mc-app-id there via buildLoginHeaders.
  headers['x-mc-date'] = new Date().toUTCString();

  if (appId) {
    headers['x-mc-app-id'] = appId;
  }

  const transformed = request.clone({
    setHeaders: headers,
    body: formatLoginApiPayload(request.body),
  });

  return next(transformed);
};
